import React, { FunctionComponent, useCallback, useMemo } from 'react';
import cx from 'ui/helper/prefixed-class-names';
import Translate from 'ui/atoms/translate';
import StatusTag from 'ui/atoms/status-tag';
import { UITransfer } from 'ui/types/transfer';
import { transferStatusToVariant } from 'ui/helper/variant';
import { DataRecordOrdering } from 'ui/types/data-ordering';
import DataRecordTable, { CellTypeEnum } from 'libraries/data-record-table';
import Number from 'ui/atoms/number';
import { PaginationProps } from 'ui/molecules/pagination';
import {
  TokenTransfersApi,
  TokenTransfersListOrderingEnum,
  TokenTransfersListStatusEnum,
  TokenTransfersValuesRetrieveFieldEnum,
} from 'src/api';
import { camelCase } from 'lodash';
import useApiCall from 'src/hooks/use-api-call';
import { useTranslateWithStringArgs } from 'src/ui/hooks/use-translate';
import { snakeToCamelCaseWithNumbers } from 'src/ui/helper/case-transforms';

export interface TransferTableProps {
  className?: string;
  ordering?: DataRecordOrdering;
  onOrderBy?: (fieldName: string) => void;
  transfers?: UITransfer[];
  goToDetails: (transferId: string) => void;
  paginationProps?: PaginationProps;
  skipFilterUrlSync?: boolean;
}

const TransferTable: FunctionComponent<TransferTableProps> = (props) => {
  const { className, ordering, onOrderBy, transfers, goToDetails, paginationProps } = props;

  const { makeAuthenticatedApi } = useApiCall();
  const translate = useTranslateWithStringArgs();
  const tokenTransfersApi: TokenTransfersApi = useMemo(
    () => makeAuthenticatedApi(TokenTransfersApi),
    [makeAuthenticatedApi],
  );

  const transferStatusFilterOptions = useMemo(() => {
    return Object.values(TokenTransfersListStatusEnum).map((status) => ({
      id: status,
      name: translate(`portfolio.transferStatus.${snakeToCamelCaseWithNumbers(status)}`),
    }));
  }, [translate]);

  const getSenderAndReceiverValues = useCallback(
    (options: string[]) => {
      return (
        options
          // Remove options that contain {{BURN}} from the array
          .filter((str) => !str.includes('{{BURN}}'))
          .map((str) => {
            const replacedString = str
              // Replace {{WALLET_<WALLET_TYPE>}} with proper translation
              .replace(/\{\{WALLET_([^}]+)\}\}/g, (_, walletType) =>
                translate('transferIdentifier.yourWallet', {
                  args: [translate(`walletType.${walletType.toLowerCase()}`)],
                }),
              )
              // Replace {{ISSUANCE}} with proper translation
              .replace('{{ISSUANCE}}', translate('transferIdentifier.issuance'));
            return {
              value: str,
              label: replacedString,
              name: replacedString,
            };
          })
      );
    },
    [translate],
  );

  return (
    <div className={cx('transfer-table', className)}>
      <DataRecordTable
        onOrderBy={onOrderBy}
        ordering={ordering}
        data={transfers}
        paginationProps={paginationProps}
        onGoToDetails={goToDetails}
        cells={[
          {
            title: <Translate name="portfolio.asset" />,
            render: (transfer) => transfer.productName,
            orderingFieldName: TokenTransfersListOrderingEnum.PRODUCT_NAME_ASC,
            filterName: 'productName',
            searchFieldName: TokenTransfersValuesRetrieveFieldEnum.PRODUCT_NAME,
            getFilterData: (params: any) => tokenTransfersApi.tokenTransfersValuesRetrieve(params),
          },
          {
            title: <Translate name="transactionsTable.sender" />,
            render: ({ isIssuance, fromWallet, fromAddress }) => {
              return isIssuance ? (
                <Translate name={`transferIdentifier.issuance`} />
              ) : fromWallet ? (
                <Translate
                  name="transferIdentifier.yourWallet"
                  args={[<Translate key={0} name={`walletType.${camelCase(fromWallet.type)}`} />]}
                />
              ) : (
                `${fromAddress.slice(0, 10)}...`
              );
            },
            orderingFieldName: TokenTransfersListOrderingEnum.SENDER_COLUMN_ASC,
            type: CellTypeEnum.text,
            filterName: 'senderColumn',
            searchFieldName: TokenTransfersValuesRetrieveFieldEnum.SENDER_COLUMN,
            getFilterData: (params: any) => tokenTransfersApi.tokenTransfersValuesRetrieve({ ...params }),
            processFilterData: (data: string[]) => getSenderAndReceiverValues(data),
          },
          {
            title: <Translate name="transactionsTable.recipient" />,
            render: ({ toWallet, toAddress }) => {
              return toWallet ? (
                <Translate
                  name="transferIdentifier.yourWallet"
                  args={[<Translate key={0} name={`walletType.${camelCase(toWallet.type)}`} />]}
                />
              ) : toAddress === '0x0000000000000000000000000000000000000000' ? (
                <Translate name={`transferIdentifier.issuance`} />
              ) : (
                `${toAddress.slice(0, 10)}...`
              );
            },
            orderingFieldName: TokenTransfersListOrderingEnum.RECIPIENT_COLUMN_ASC,
            type: CellTypeEnum.text,
            filterName: 'recipientColumn',
            searchFieldName: TokenTransfersValuesRetrieveFieldEnum.RECIPIENT_COLUMN,
            getFilterData: (params: any) => tokenTransfersApi.tokenTransfersValuesRetrieve({ ...params }),
            processFilterData: (data: string[]) => getSenderAndReceiverValues(data),
          },
          {
            title: <Translate name="portfolio.amount" />,
            alignContent: 'right',
            render: (transfer) => <Number>{transfer.amount}</Number>,
            orderingFieldName: TokenTransfersListOrderingEnum.AMOUNT_ASC,
            type: CellTypeEnum.number,
            filterName: 'amount',
          },
          {
            title: <Translate name="common.status" />,
            render: ({ status }) => (
              <StatusTag variant={transferStatusToVariant(status)}>
                <Translate name={`portfolio.transferStatus.${snakeToCamelCaseWithNumbers(status)}`}>{status}</Translate>
              </StatusTag>
            ),
            orderingFieldName: TokenTransfersListOrderingEnum.STATUS_ASC,
            type: CellTypeEnum.text,
            filterName: 'status',
            getFilterData: () => {
              return transferStatusFilterOptions;
            },
          },
        ]}
      />
    </div>
  );
};

export default TransferTable;
